import { WizardStep } from '@app/app/_components/Wizard/Wizard';
import { useCallback, useMemo, useState } from 'react';

export function useWizard(steps: WizardStep[]) {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [currentSubStepIndex, setCurrentSubStepIndex] = useState(0);

    const filteredSteps = useMemo(
        () =>
            steps
                .map(step => ({ ...step, subSteps: step.subSteps.filter(subStep => subStep.when?.() ?? true) }))
                .filter(step => step.subSteps.length !== 0),
        [steps],
    );

    const nextStep = useCallback(() => {
        if (currentStepIndex >= filteredSteps.length - 1) {
            return;
        }

        setCurrentStepIndex(prevStepIndex => prevStepIndex + 1);
        setCurrentSubStepIndex(0);
    }, [currentStepIndex, filteredSteps.length]);

    const prevStep = useCallback(() => {
        if (currentStepIndex <= 0) {
            return;
        }

        const prevStepIndex = currentStepIndex - 1;
        const prevStep = filteredSteps[prevStepIndex];

        setCurrentStepIndex(prevStepIndex);
        setCurrentSubStepIndex(prevStep.subSteps.length - 1);
    }, [currentStepIndex, filteredSteps]);

    const handleBackClick = useCallback(() => {
        if (currentSubStepIndex <= 0) {
            return prevStep();
        }

        setCurrentSubStepIndex(prevSubStepIndex => prevSubStepIndex - 1);
    }, [currentSubStepIndex, prevStep]);

    const handleNextClick = useCallback(() => {
        const currentStep = filteredSteps[currentStepIndex];

        if (currentSubStepIndex >= currentStep.subSteps.length - 1) {
            return nextStep();
        }

        setCurrentSubStepIndex(prevSubStepIndex => prevSubStepIndex + 1);
    }, [currentStepIndex, currentSubStepIndex, nextStep, filteredSteps]);

    const currentStepKey = useMemo(() => {
        return filteredSteps[currentStepIndex].subSteps[currentSubStepIndex].key;
    }, [currentStepIndex, currentSubStepIndex, filteredSteps]);

    const setCurrentStepKey = useCallback(
        (key: string) => {
            for (let stepIndex = 0; stepIndex < filteredSteps.length; stepIndex++) {
                const step = filteredSteps[stepIndex];
                for (let subStepIndex = 0; subStepIndex < step.subSteps.length; subStepIndex++) {
                    if (step.subSteps[subStepIndex].key === key) {
                        setCurrentStepIndex(stepIndex);
                        setCurrentSubStepIndex(subStepIndex);
                        return;
                    }
                }
            }
        },
        [filteredSteps],
    );

    const isFirstStep = useMemo(() => {
        return currentStepIndex === 0 && currentSubStepIndex === 0;
    }, [currentStepIndex, currentSubStepIndex]);

    const isFinalStep = useMemo(() => {
        return (
            currentStepIndex === filteredSteps.length - 1 &&
            currentSubStepIndex === filteredSteps[currentStepIndex].subSteps.length - 1
        );
    }, [currentStepIndex, currentSubStepIndex, filteredSteps]);

    return {
        filteredSteps,
        currentStepIndex,
        currentSubStepIndex,
        currentStepKey,
        setCurrentStepKey,
        isFirstStep,
        isFinalStep,
        next: handleNextClick,
        back: handleBackClick,
    };
}
